// ContentPage — the account/company page skeleton, on the SAME parts the
// documents pages use (2026-09-02 per Nika "fix ui of /profile page. it
// should match all other pageslots (take settings and notes as example).
// make all layouts pages consistent"):
//   • rail   = AppSidebar (the docs DocsSidebar base): width G, Z/X2 inset,
//              a CommonCard Middle holding SidebarRow links — the notes rail.
//   • main   = AppMain: a sticky PageHeader (title + eyebrow, actions slot)
//              over a scrolling Body with the docs' 'B C' inset.
// Before: its own skeleton — a D C outer inset (72/44px), a sticky 305px
// rail with eyebrow / h1 / hairline / pill links, no header bar — so the
// profile sheet sat beside the notes sheet as a different language.
// Consumers (profile, my, company) mount their content at
// `PageMain.Body.PageBody`; `eyebrow` / `title` / `sections` stay page props.
export const ContentPage = {
  extends: 'Page',
  flow: 'x',
  align: 'stretch',
  gap: '0',
  padding: '0',
  margin: '0',
  width: '100%',
  height: '100%',
  minHeight: '0',
  minWidth: '0',
  maxWidth: 'none',
  boxSizing: 'border-box',
  overflow: 'hidden',

  '@mobileL': {
    flow: 'y',
    overflowX: 'clip'
  },
  // The pane twin. This page extends `Page`, not `AppPage`, so the shared
  // shell's `@appMobile` posture never reached it: in a 715px pane the rail
  // took AppSidebar's stacked width (100%) while this row stayed a row, and
  // PageMain collapsed to 0px past the pane's right edge — /my showed its
  // section list and nothing else (measured live 2026-09-22;
  // __tests__/contentPagePanePosture.test.js).
  '@appMobile': {
    flow: 'y',
    overflowX: 'clip'
  },

  PageRail: {
    extends: 'AppSidebar',
    tag: 'nav',
    // On the page-sidebar rule (shared functions/pageSidebar.js) for every
    // content page (/my, /company, /profile): opposite a rail navbar,
    // hideable per page.
    pageSidebar: true,
    ViewSwitcher: null,
    Top: null,
    PeopleSection: null,

    // The section links — one CommonCard (AppSidebar's childExtends), the
    // notes rail's Middle. Rows are per-section child STATES. They start at
    // the card's top: the head band keeps the hide button off the first link.
    Middle: {
      'data-sidebar-band': 'true',
      SectionNav: {
        flow: 'y',
        gap: 'V',
        padding: 'X2',
        '@mobileL': {
          flow: 'x',
          overflowX: 'auto'
        },
        '@appMobile': {
          flow: 'x',
          overflowX: 'auto'
        },
        childExtends: 'ContentPageSectionLink',
        childrenAs: 'state',
        children: (el, s) => {
          // SectionNav → Middle → PageRail → the page
          const page = el.parent?.parent?.parent
          if (!page) return []
          const sections = page.sections
          const list = typeof sections === 'function' ? sections(el, s) : sections || []
          // `__i` only — the row decides ACTIVE itself, from the root key, on
          // its `aria-current` factory (the one channel that re-runs here; the
          // paint hangs off that attribute in the shell sheet). A flag stamped
          // at create would be frozen: nothing re-renders these rows, and a
          // `subscribeTo` on this list did not change that (measured live).
          return list.map((sec, i) => ({ ...sec, __i: i }))
        }
      }
    }
  },

  PageMain: {
    extends: 'AppMain',

    Header: {
      extends: 'PageHeader',
      LeftGroup: {
        flow: 'y',
        gap: 'V',
        align: 'flex-start left',
        // The notes header's title/count pair: the page title, and the eyebrow
        // ("Account") as the caption line under it. The title is the page's
        // one `h1` at its dedicated URL and an `h2` under a pane bar
        // (design-direction.md §3) — it was a literal `h2`, so /my, /company
        // and /profile had no `h1` at all.
        Title: {
          tag: (el, s) => el.call('pageTitleTag', s) || 'h1',
          margin: '0',
          fontSize: 'C',
          fontWeight: '700',
          color: 'title',
          // Title → LeftGroup → Header → PageMain → the page (the one
          // `title`-as-content read this package keeps — I18N-CONVENTION §1.2).
          text: (el) => el.parent.parent.parent.parent.title || ''
        },
        Eyebrow: {
          tag: 'span',
          fontSize: 'Z2',
          color: 'caption',
          hide: (el) => !el.parent.parent.parent.parent.eyebrow,
          text: (el) => el.parent.parent.parent.parent.eyebrow || ''
        }
      },
      RightGroup: {}
    },

    Body: {
      padding: 'B C',
      overflow: 'auto',
      '@mobileL': { padding: 'A' },
      '@appMobile': { padding: 'A' },

      PageBody: {
        flow: 'y',
        minWidth: '0',
        maxWidth: '100%'
      }
    }
  }
}

// A rail row — the notes rail's DocsSectionRow shape on the shared SidebarRow
// (icon optional, label, active wash `selected`), not a bespoke pill button.
export const ContentPageSectionLink = {
  extends: 'SidebarRow',
  fontWeight: '700',
  // ACTIVE STATE TRAVELS ON THE ATTRIBUTE, THE PAINT LIVES IN THE SHELL SHEET.
  // Measured live 2026-09-02 on /w/natali/…/profile: on these
  // `childrenAs: 'state'` rows every STYLE channel is frozen at create — an
  // `isActive` conditional class never re-attached, `background`/`color`
  // prop factories never re-ran, `subscribeTo: ['contentSection']` on the row
  // AND on the list changed nothing (root.contentSection reads correctly the
  // whole time, and the row nodes are never replaced). The `aria-current`
  // ATTRIBUTE on the same element followed every single click. A component-
  // level `&[aria-current="true"]` selector block did not paint either — an
  // atomic class beat it even at higher specificity — so the rule lives in
  // the shell's injected stylesheet (app.js), the same reacts-in-CSS idiom as
  // `html[data-chat-drawer="open"] …`, which does outrank atomic classes.
  'aria-current': (el, s) => {
    const current = s.root?.contentSection || null
    const active = current ? current === (s.sectionKey || s.label || null) : s.__i === 0
    return active ? 'true' : undefined
  },
  ':hover': { background: 'hover', color: 'title' },
  '@mobileL': {
    flex: '0 0 auto',
    whiteSpace: 'nowrap'
  },
  Icon: {
    hide: (el, s) => !s.icon,
    icon: (el, s) => s.icon || 'state'
  },
  Label: { text: (el, s) => s.label || '' },
  MoreBtn: null,
  onClick: (e, el, s) => {
    e.preventDefault()
    s.root.update({ contentSection: s.sectionKey || s.label })
    setTimeout(() => {
      try {
        const target = document.querySelector(
          `[data-content-section="${(s.sectionKey || s.label || '').replace(/"/g, '\\"')}"]`
        )
        if (!target) return
        // Skip scroll when the target section is hidden (display:none sets
        // offsetHeight to 0). This happens when a section is conditionally
        // excluded for the current user (e.g. Availability for non-members).
        if (!target.offsetHeight) return
        let scroller = target.parentElement
        while (scroller) {
          const cs = getComputedStyle(scroller)
          if (
            (cs.overflowY === 'auto' || cs.overflowY === 'scroll') &&
            scroller.scrollHeight > scroller.clientHeight + 5
          )
            break
          scroller = scroller.parentElement
        }
        if (scroller) {
          const offset = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top
          scroller.scrollTop = scroller.scrollTop + offset - 16
        } else {
          target.scrollIntoView({ block: 'start' })
        }
      } catch {}
    }, 0)
  }
}
